import { Injectable } from "@angular/core";
import { MatLegacyDialog } from "@angular/material/legacy-dialog";
import { CtBinaryOperator, CtControlConfiguration, CtControlService, CtControlTypes, CtModelConfiguration, CTModelDatatableFilter, CtModelDatatableOperators, CtSchedulerEvent, CtSchedulerEventFormComponent, CtSchedulerEventFormConfiguration, CtSelectControlOptions, CtSelectControlValue, CtSortOrderDescriptorParameter, CTFormArrayConfiguration, MateriaHintExtensionConfig, MaterialControlOptions, CtControlValidator } from "@ctsolution/ct-framework";
import { ActivatedRoute, Router } from "@angular/router";
import { CTMGeneralService } from "../../_core/lib/general.service";
import { DataRequest, MethodEnum } from "@ctsolution/ct-webapi";
import { JwtService } from "../../_core/lib/jwt.service";
import { EventPathParam, EventTypeEnum } from "../../_core/classes/event-path-param";
import { CtManagerService } from "../../_core/lib/ct-manager.service";
import { CTConfigurationModel } from "../../_core/classes/ct-configuration-model";
import { FormBuilder, Validators, AbstractControl, ValidatorFn } from "@angular/forms";

@Injectable({
  providedIn: 'root'
})
export class SchedulerService {

  agentControl = this.controlService
    .getDefaultConfiguration({
      name: 'Agent',
      type: CtControlTypes.LOOKUP,
      remoteValueOptionsEndpoint: DataRequest
        .create()
        .setController(['User'])
        .setAction('Lookup')
        .setMethod(MethodEnum.POST)
        .setQueryParams({ hideSpinner: true })
    })
    .setLabel('OwnerUser')
    .setFlexSpacePercentage(50)
    .configureOptions<CtSelectControlOptions>(options => {

      if (!options) options = CtSelectControlOptions.create();

      options
        .setNullValueEnabled(true)
        .setLookupFilter((value) => {

          const filters: CTModelDatatableFilter[] = [];

          if (value) {

            filters
              .push(
                CTModelDatatableFilter
                  .create()
                  .setField("NameSurname")
                  .setValue(value)
                  .setOperatorType(CtBinaryOperator.Contains)
              );
          }

          const operators = CtModelDatatableOperators
            .create()
            .setFilters(filters);

          operators
            .Pagination
            ?.setSortOrders(new Array<CtSortOrderDescriptorParameter>(CtSortOrderDescriptorParameter.create('Name')))

          return operators;

        })

    });

  companyControl = this.controlService
    .getDefaultConfiguration({
      name: 'CompanyPlace',
      type: CtControlTypes.LOOKUP,
      remoteValueOptionsEndpoint: DataRequest
        .create()
        .setController(['CompanyPlace'])
        .setAction('Lookup')
        .setMethod(MethodEnum.POST)
        .setQueryParams({ hideSpinner: true })
    })
    .setLabel('MyCompanyPlace')
    .setFlexSpacePercentage(50)
    .configureOptions<CtSelectControlOptions>(options => {

      if (!options) options = CtSelectControlOptions.create();

      options
        .setNullValueEnabled(true)
        .setLookupFilter((value) => {

          const filters: CTModelDatatableFilter[] = []

          filters
            .push(
              CTModelDatatableFilter
                .create()
                .setField('Company.IsSelf')
                .setValue(true)
                .setOperatorType(CtBinaryOperator.Equal)
            );

          const operators = CtModelDatatableOperators
            .create()
            .setFilters(filters);

          operators
            .Pagination
            ?.setSortOrders(new Array<CtSortOrderDescriptorParameter>(CtSortOrderDescriptorParameter.create('Name')))

          return operators;

        })

    });

  ExternalEmailsControl = this.controlService
    .getDefaultConfiguration({
      name: 'ExternalEmails',
      type: CtControlTypes.TEXTAREA
    })
    .setLabel('Email esterne aggiuntive')
    .setFlexSpacePercentage(100)
    .setPlaceholder('esempio@email.it ; esempio@email.com')
    .setValidators([
      CtControlValidator.create()
        .setName('multipleEmails')
        .setCustomValidatorFn(this.multipleEmailsValidator())
        .setErrorMessage('multiple_emails_invalid')
    ])

  constructor(
    private dialog: MatLegacyDialog,
    private router: Router,
    private route: ActivatedRoute,
    private general: CTMGeneralService,
    private controlService: CtControlService,
    private jwtService: JwtService,
    private managerService: CtManagerService,
    private formBuilder: FormBuilder
  ) {
  }

  /**
   * Validatore custom per email multiple separate da punto e virgola.
   * 
   * Accetta:
   * - Campo vuoto (0 email)
   * - Una o più email separate da punto e virgola
   * - Spazi opzionali prima e dopo ogni email e il separatore
   * 
   * Esempi validi:
   * - ""
   * - "test@example.com"
   * - "test@example.com;another@test.it"
   * - "test@example.com ; another@test.it"
   * - "test@example.com; another@test.it ;third@email.com"
   * 
   * Esempi non validi:
   * - "notanemail" (email malformata)
   * - "test@example.com;" (termina con separatore)
   * - "test@example;invalid@" (email incomplete)
   * - "test@example.com;notvalid" (una delle email non è valida)
   * - "esempio@esempio" (dominio senza estensione .com, .it, etc.)
   * 
   * @returns ValidatorFn che ritorna null se valido, o oggetto errore con dettagli
   */
  private multipleEmailsValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;

      // Campo vuoto è considerato valido (se serve required, aggiungere validatore apposito)
      if (!value || value.trim() === '') {
        return null;
      }

      // Regex per validare singola email
      // Pattern robusto che copre la maggior parte dei casi reali
      // IMPORTANTE: Richiede almeno un punto (.) nel dominio per forzare la presenza del TLD
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

      // Splitta per punto e virgola
      const emailsArray = value.split(';');

      // Array per tracciare email invalide con dettagli
      const invalidEmails: { email: string; position: number; reason: string }[] = [];

      // Valida ogni email
      for (let i = 0; i < emailsArray.length; i++) {
        const email = emailsArray[i].trim(); // Rimuove spazi all'inizio e fine

        // Controlla se è l'ultimo elemento e se è vuoto (caso: "email1;email2;")
        if (i === emailsArray.length - 1 && email === '') {
          invalidEmails.push({
            email: '(vuoto)',
            position: i + 1,
            reason: 'Il campo termina con un punto e virgola superfluo'
          });
          continue;
        }

        // Controlla se l'email è vuota (caso: "email1;;email2")
        if (email === '') {
          invalidEmails.push({
            email: '(vuoto)',
            position: i + 1,
            reason: 'Email vuota tra i separatori'
          });
          continue;
        }

        // Valida il formato email
        if (!emailRegex.test(email)) {
          // Determina il motivo specifico dell'errore per dare feedback migliore
          let reason = 'Formato email non valido';

          if (!email.includes('@')) {
            reason = 'Manca il simbolo @';
          } else if (email.startsWith('@')) {
            reason = 'Email inizia con @';
          } else if (email.endsWith('@')) {
            reason = 'Email termina con @';
          } else if (email.split('@').length > 2) {
            reason = 'Troppi simboli @';
          } else {
            const parts = email.split('@');
            if (parts[0].length === 0) {
              reason = 'Parte locale vuota (prima di @)';
            } else if (parts[1].length === 0) {
              reason = 'Dominio vuoto (dopo @)';
            } else if (!parts[1].includes('.')) {
              reason = 'Dominio senza estensione (es: .com, .it)';
            } else if (parts[1].endsWith('.')) {
              reason = 'Dominio termina con un punto';
            } else if (parts[1].startsWith('.')) {
              reason = 'Dominio inizia con un punto';
            } else {
              // Controllo aggiuntivo: verifica che ci sia testo dopo l'ultimo punto
              const domainParts = parts[1].split('.');
              const tld = domainParts[domainParts.length - 1];
              if (!tld || tld.length === 0) {
                reason = 'Estensione dominio mancante o non valida';
              } else if (tld.length === 1) {
                reason = 'Estensione dominio troppo corta (minimo 2 caratteri)';
              }
            }
          }

          invalidEmails.push({
            email: email,
            position: i + 1,
            reason: reason
          });
        }
      }

      // Se ci sono email invalide, ritorna errore con dettagli
      if (invalidEmails.length > 0) {
        return {
          multipleEmails: {
            invalidCount: invalidEmails.length,
            totalCount: emailsArray.length,
            invalidEmails: invalidEmails,
            // Messaggio user-friendly per il primo errore
            message: invalidEmails.length === 1
              ? `Email ${invalidEmails[0].position} non valida: "${invalidEmails[0].email}" - ${invalidEmails[0].reason}`
              : `${invalidEmails.length} email non valide su ${emailsArray.length}. Prima: "${invalidEmails[0].email}" - ${invalidEmails[0].reason}`
          }
        };
      }

      // Tutto valido
      return null;
    };
  }

  async createEventFromPath(modelConfiguration: CtModelConfiguration<any>, param?: EventPathParam | null, value?: CtSchedulerEvent | null, event?: any) {

    let eventValue: CtSchedulerEvent | null = null;

    // Imposta valori di default solo per nuovi eventi
    if (!value) {
      await this.setDefaultUser().then(() => { return });
      await this.setDefaultCompanyPlace().then(() => { return });
    }

    const schedulerInfoConfiguration: CTConfigurationModel = CTConfigurationModel.create();

    await this.managerService.getComponentOverride("scheduler", "schedulerInfoConfiguration", schedulerInfoConfiguration).then((component) => {
      return;
    })

    const materialOptions = this.ExternalEmailsControl.materialOptions;

    if (!materialOptions) {

      this.ExternalEmailsControl.setMaterialOptions(MaterialControlOptions.create());

    }

    this.ExternalEmailsControl.materialOptions
      ?.setHint(MateriaHintExtensionConfig.create().setMessage('Separa gli indirizzi con “;” per inviare la calendarizzazione anche a contatti esterni.'));


    const configuration = CtSchedulerEventFormConfiguration
      .create()
      .setModelConfiguration(modelConfiguration)
      .setAdditionalFormInfos([this.ExternalEmailsControl, this.companyControl, this.agentControl])
      .setSetupAdditionalForm(this.setupAddiotionForm)

    if (schedulerInfoConfiguration.valueOptions) {

      const options = this.getObjectValueOptions(schedulerInfoConfiguration.valueOptions)

      configuration.setTypeValueOptions(options)

    }


    if (value) {

      eventValue = value;

      if (typeof eventValue.Start === 'string') {
        eventValue.Start = new Date(eventValue.Start);
      }

      if (typeof eventValue.End === 'string') {
        eventValue.End = new Date(eventValue.End);
      }

      configuration
        .setEventValue(eventValue)

      // Carica gli utenti già assegnati nel FormArray
      // if (eventValue.Users && Array.isArray(eventValue.Users)) {
      //   await this.loadExistingUsers(eventValue.Users, configuration);
      // }

      if (value.Origin && value.ExternalOid) {
        const subtitle = await this.getSubtitle(+value.Origin, value.ExternalOid);
        if (subtitle) (<any>eventValue).SubTitle = subtitle;

        // Verifica se il work-order è una quotation per costruire il path corretto
        const isQuotation = await this.checkIfWorkOrderIsQuotation(+value.Origin, value.ExternalOid);

        // Se è una quotation, modifica l'Origin per cambiare la label del bottone
        // Il framework userà 'CT_CALENDAR.' + Origin per la traduzione
        // Usiamo il valore numerico 6 per QUOTATION (anche se non è in CtSchedulerOriginEnum del framework)
        if (isQuotation && +value.Origin === 2) { // 2 = WorkOrder
          (<any>eventValue).Origin = 6; // 6 = Quotation
        }

        configuration.setNavigateSubmit(() => {
          this.router.navigate([this.getPathToReturn(+value.Origin, isQuotation), 'edit', value.ExternalOid])
          this.dialog.closeAll();
        })

      }
    }

    if (param?.Oid && param?.Type) {
      eventValue = <CtSchedulerEvent>{ Start: event.date }
      eventValue.Origin = +param?.Type;
      eventValue.ExternalOid = param?.Oid;

      const subtitle = await this.getSubtitle(+param?.Type, +param?.Oid);
      if (subtitle) (<any>eventValue).SubTitle = subtitle;



      configuration.setEventValue(eventValue);
    }

    if (eventValue == null && event) {
      configuration.setEventValue(<CtSchedulerEvent>{ Start: event.date });

    }


    return this.dialog
      .open(CtSchedulerEventFormComponent, { disableClose: true, maxWidth: 800, data: configuration })
      .afterClosed().toPromise();

  }

  /**
   * Ritorna il path corretto in base al tipo di entità (origin) e se è una quotation
   * @param origin Tipo di entità (numero: 1=Lead, 2=WorkOrder, 6=Quotation)
   * @param isQuotation Se true, per i work-order ritorna 'work-order/quotations', altrimenti 'work-order'
   * @param extraUrl URL extra per customer
   * @returns Il path di navigazione corretto
   */
  getPathToReturn(origin: number, isQuotation: boolean = false, extraUrl: string | null = null) {

    switch (origin) {
      case 1: // Lead
        return 'lead';
      case 2: // WorkOrder
        // Se è una quotation, includi il segmento 'quotations' nel path
        return isQuotation ? 'work-order/quotations' : 'work-order';
      case 6: // Quotation
        // QUOTATION viene trattato come un work-order quotation
        return 'work-order/quotations';
      case 5: // Customer
        if (extraUrl) return `customer/${extraUrl}`
        return 'customer';
      default:
        return '';
    }

  }

  /**
   * Versione asincrona di getPathToReturn che verifica dal backend se è una quotation
   * @param origin Tipo di entità (numero: 1=Lead, 2=WorkOrder, 6=Quotation)
   * @param oid Oid dell'entità
   * @param extraUrl URL extra per customer
   * @returns Promise<string> Il path di navigazione corretto
   */
  async getPathToReturnAsync(origin: number, oid: number, extraUrl: string | null = null): Promise<string> {
    // Per i work-order e quotation, verifica se è una quotation
    if (origin === 2 || origin === 6) { // 2 = WorkOrder, 6 = Quotation
      const isQuotation = await this.checkIfWorkOrderIsQuotation(origin, oid);
      return this.getPathToReturn(origin, isQuotation, extraUrl);
    }

    // Per altri tipi, usa il metodo sincrono standard
    return this.getPathToReturn(origin, false, extraUrl);
  }

  setupAddiotionForm(controls: Array<CtControlConfiguration>, value: any) {

    if (value.CompanyPlace) {

      controls.find(control => control.name === "CompanyPlace")?.setValue(CtSelectControlValue.create().setLabel(value.CompanyPlace.Name ?? "Dato sconosciuto").setValue(value.CompanyPlace.Oid));

    } else if (controls.find(control => control.name === "CompanyPlace")?.value) {

      controls.find(control => control.name === "CompanyPlace")?.control?.setValue(controls.find(control => control.name === "CompanyPlace")?.value);

    }

    if (value.Agent) {

      controls.find(control => control.name === "Agent")?.setValue(CtSelectControlValue.create().setLabel(value.Agent.NameSurname ?? "Dato sconosciuto").setValue(value.Agent.Oid));

    } else if (controls.find(control => control.name === "Agent")?.value) {

      controls.find(control => control.name === "Agent")?.control?.setValue(controls.find(control => control.name === "Agent")?.value);

    }

  }

  navigateToPath(path: EventPathParam) {

    const queryParams = {
      TypeExtra: path.Type,
      OidExtra: path.Oid
    };

    // Naviga verso il nuovo path mantenendo eventuali altri queryParams esistenti
    this.router.navigate(['scheduler'], { queryParamsHandling: 'merge', queryParams }
    );


  }


  returnParam(id: number | null = null) {

    const pId = this.route.snapshot.paramMap.get("id");
    let param: EventPathParam | null = null;
    const currentUrl = this.router.url;
    let type: EventTypeEnum | null = null;

    if (currentUrl.includes('lead')) {
      type = EventTypeEnum.LEAD;
    } else if (currentUrl.includes('work-order')) {
      type = EventTypeEnum.WORKORDER;
    } else if (currentUrl.includes('customer')) {
      type = EventTypeEnum.CUSTOMER;
    }


    if (id)
      param = EventPathParam.create(id, type);
    else
      if (pId)
        param = EventPathParam.create(+pId, type);

    return param

  }


  getSubtitle(origin: number, oid: number) {

    switch (origin) {

      case 1: // Lead
        return this.getLeadSubtitle(oid);

      case 2: // WorkOrder
      case 6: // Quotation
        // Sia WORKORDER che QUOTATION usano lo stesso metodo
        return this.getWorkOrderSubtitle(oid);

      default:
        return "";


    }


  }

  private getLeadSubtitle(oid: number): Promise<string> {

    return new Promise<any | null>(resolve => {
      this.general.getInfos(
        {
          controller: 'Lead',
          oid: oid
        }
      ).subscribe(result => {

        if (result.Result?.DataSource) {

          const res = [];
          const value = result.Result.DataSource;

          if (value.Name)
            res.push(`Nome: ${value.Name}`);

          if (value.Surname)
            res.push(`Cognome: ${value.Surname}`);


          if (value.Email)
            res.push(`Email: ${value.Email}`);

          if (value.Phone)
            res.push(`Telefono: ${value.Phone}`);

          resolve(res)

        }

      })

    })

  }


  private getWorkOrderSubtitle(oid: number) {

    return new Promise<any | null>(resolve => {
      this.general.getInfos(
        {
          controller: 'WorkOrder',
          oid: oid
        }
      ).subscribe(result => {

        if (result.Result?.DataSource) {

          const res = [];
          const value = result.Result.DataSource;


          if (value.Company?.DisplayName)
            res.push(`Cliente: ${value.Company?.DisplayName}`);

          if (value.Name)
            res.push(`Nome: ${value.Name}`);

          if (value.Code)
            res.push(`Codice: ${value.Code}`);

          resolve(res)

        }

      })

    })

  }

  async setDefaultUser() {

    const oid = await this.jwtService.getUserInfoOid();

    if (oid) {

      return new Promise<void>(resolve => {
        this.general.getInfos(
          {
            controller: 'User',
            oid: +oid
          }
        ).subscribe(result => {

          if (result.Result?.DataSource) {

            this.agentControl.setValue(CtSelectControlValue.create().setLabel(result.Result.DataSource.NameSurname ?? "Dato sconosciuto").setValue(+oid));
            resolve()

          }
        })
      })
    }
  }

  async setDefaultCompanyPlace() {

    const filters: CTModelDatatableFilter[] = []

    filters
      .push(
        CTModelDatatableFilter
          .create()
          .setField('Company.IsSelf')
          .setValue(true)
          .setOperatorType(CtBinaryOperator.Equal)
      );

    return new Promise<void>(resolve => {
      this.general.getList(
        {
          controller: 'CompanyPlace',
          filters: filters
        }
      ).subscribe(result => {

        if (result.Result?.DataSource && result.Result?.DataSource.length) {

          this.companyControl.setValue(CtSelectControlValue.create().setLabel(result.Result.DataSource[0].Name ?? "Dato sconosciuto").setValue(result.Result.DataSource[0].Oid));
          this.companyControl.control?.updateValueAndValidity();

        }

        resolve()

      })
    })
  }

  /**
   * Verifica se un work-order è una quotation leggendo i dati dal backend
   * @param origin Tipo di entità (numero: 2=WorkOrder, 6=Quotation)
   * @param oid Oid del work-order
   * @returns Promise<boolean> - true se è una quotation, false altrimenti
   */
  private checkIfWorkOrderIsQuotation(origin: number, oid: number): Promise<boolean> {
    // Controlla solo se l'origine è un WORKORDER o QUOTATION
    if (origin !== 2 && origin !== 6) { // 2 = WorkOrder, 6 = Quotation
      return Promise.resolve(false);
    }

    // Se l'origin è già QUOTATION (6), ritorna true direttamente
    if (origin === 6) {
      return Promise.resolve(true);
    }

    return new Promise<boolean>(resolve => {
      this.general.getInfos(
        {
          controller: 'WorkOrder',
          oid: oid
        }
      ).subscribe(result => {
        // Verifica se il campo Quotation esiste ed è true
        if (result.Result?.DataSource?.Quotation === true) {
          resolve(true);
        } else {
          resolve(false);
        }
      }, error => {
        // In caso di errore, assume che non sia una quotation
        console.error('Errore durante la verifica della quotation:', error);
        resolve(false);
      });
    });
  }

  private getObjectValueOptions(obj: Record<string, number>) {
    return Object.entries(obj).map(([label, value]) =>
      CtSelectControlValue.create()
        .setLabel(`${label}`)
        .setValue(value)
    );
  }

  /**
   * Carica gli utenti già assegnati nel FormArray
   * @param usersOid Array di OID utenti da caricare
   * @param configuration Configurazione del form scheduler
   */
  private async loadExistingUsers(
    usersOid: number[],
    configuration: CtSchedulerEventFormConfiguration<any>
  ): Promise<void> {

    if (!configuration.userFormArray || usersOid.length === 0) {
      return;
    }

    // Carica le informazioni di ogni utente e crea i controlli
    for (const userOid of usersOid) {
      const userInfo = await this.getUserInfo(userOid);

      if (userInfo) {
        // ⚠️ CRITICO: Non clonare il control esistente, crea un NUOVO control da zero
        // Il problema era che .create() condivideva il FormControl originale
        const newControl = this.controlService
          .getDefaultConfiguration({
            name: `UserFormArrayItem_${configuration.userFormArray.controlsArray.length}`,
            type: CtControlTypes.LOOKUP,
            remoteValueOptionsEndpoint: DataRequest
              .create()
              .setController(['User'])
              .setAction('Lookup')
              .setMethod(MethodEnum.POST)
              .setQueryParams({ hideSpinner: true })
          })
          .setLabel('CT_CALENDAR.SelectUser')
          .setFlexSpacePercentage(100)
          .configureOptions<CtSelectControlOptions>(options => {
            if (!options) options = CtSelectControlOptions.create();

            options
              .setNullValueEnabled(false)
              .setLookupFilter((searchValue) => {
                const filters: CTModelDatatableFilter[] = [];

                if (searchValue) {
                  filters.push(
                    CTModelDatatableFilter
                      .create()
                      .setField("NameSurname")
                      .setValue(searchValue)
                      .setOperatorType(CtBinaryOperator.Contains)
                  );
                }

                const operators = CtModelDatatableOperators
                  .create()
                  .setFilters(filters);

                operators.Pagination?.setSortOrders(
                  new Array<CtSortOrderDescriptorParameter>(
                    CtSortOrderDescriptorParameter.create('Name')
                  )
                );

                return operators;
              });
          });

        // Imposta il FormGroup padre PRIMA di settare il valore
        newControl.setFormParent(configuration.userFormArray.form!);

        // Imposta il valore con label e OID
        newControl.setValue(
          CtSelectControlValue.create()
            .setLabel(userInfo.NameSurname ?? 'Utente sconosciuto')
            .setValue(userInfo.Oid)
        );

        // Aggiungi al FormArray
        configuration.userFormArray.controlsArray.push(newControl);
      }
    }
  }

  /**
   * Recupera le informazioni di un utente dal backend
   * @param oid OID dell'utente
   * @returns Promise con i dati dell'utente o null in caso di errore
   */
  private getUserInfo(oid: number): Promise<any> {
    return new Promise((resolve) => {
      this.general.getInfos({
        controller: 'User',
        oid: oid
      }).subscribe(
        result => resolve(result.Result?.DataSource),
        error => {
          console.error(`Errore nel caricamento utente ${oid}:`, error);
          resolve(null);
        }
      );
    });
  }

}
